import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectGenAiFilter } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Gen AI filters
 * Filters on:
 * - 'gen-ai-all-features' → filters by moment.filters.genai (boolean)
 * - 'gen-ai-consumes-credits' → filters by moment.filters.consumesCredits (boolean)
 */
export const useFilterByGenAi = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected Gen AI filters from Redux state
  const genAiFilter = useSelector(selectGenAiFilter);
  const selectedFilters = genAiFilter?.selectedFilters || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no Gen AI filters are selected, return original data
    if (selectedFilters.length === 0) {
      return milestonesData;
    }

    // Check if Gen AI filters are active
    const hasAllFeaturesFilter = selectedFilters.includes(
      'gen-ai-all-features'
    );
    const hasConsumesCreditsFilter = selectedFilters.includes(
      'gen-ai-consumes-credits'
    );

    // If no Gen AI filters are active, return original data
    if (!hasAllFeaturesFilter && !hasConsumesCreditsFilter) {
      return milestonesData;
    }

    // Filter the data based on Gen AI filters
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      // Include moment if any of the selected filters match
      if (hasAllFeaturesFilter && moment.filters?.genai === true) {
        return true;
      }
      if (
        hasConsumesCreditsFilter &&
        moment.filters?.consumesCredits === true
      ) {
        return true;
      }
      return false;
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedFilters]);

  return filteredData;
};
